import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";

import Box from "components/Box";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import { usePageTitle } from "components/usePageTitle";

import ChipsGroup from "entities/AnalyticsFilterGroup/components/ChipsGroup";
import EditFilterGroupButton from "entities/AnalyticsFilterGroup/components/EditFilterGroupButton";
import AnalyticsTable from "entities/AnalyticsFilterGroup/components/IncomeAndOpexTable";
import AnalyticsDetailTabs from "entities/AnalyticsFilterGroup/components/Tabs";
import { AnalyticsIncomeAndOpexTabs } from "entities/AnalyticsFilterGroup/components/Tabs/constants";
import {
  ANALYTICS_TYPES,
  QUARTER_OPTIONS,
  updateFilter,
  useFilterGroup,
} from "entities/AnalyticsFilterGroup/sdk";
import { getConstructionTypeLabelByValue } from "entities/Deal/utils";

const tabs = [
  { label: "Income", value: AnalyticsIncomeAndOpexTabs.INCOME },
  { label: "Expenses", value: AnalyticsIncomeAndOpexTabs.EXPENSES },
  { label: "Metrics", value: AnalyticsIncomeAndOpexTabs.METRICS },
];

const AnalyticsIncomeAndOPEXDetails: React.FC<{}> = () => {
  usePageTitle("Builders Patch: Analytics - Income and OPEX");
  const [selectedAnalyticsType, setSelectedAnalyticsType] =
    useState<ANALYTICS_TYPES>(ANALYTICS_TYPES.INCOME_AND_OPEX);
  const [selectedYear, setSelectedYear] = useState("2022");
  const [selectedQuarter, setSelectedQuarter] = useState<QUARTER_OPTIONS>(
    QUARTER_OPTIONS.FULL_YEAR
  );
  const { id } = useParams<{ id: string }>();

  /*
    Here we can call two URLs based on selectedAnalyticsType:
    - v2/analytics/filter-group/${id}/income-and-opex/details/
    - v2/analytics/filter-group/${id}/asset-management/details/?year=${year}&quarter=${quarter}
  */
  const {
    data: filterGroup,
    mutate: updateFilterGroup,
    loading,
    isValidating,
  } = useFilterGroup(id, selectedAnalyticsType, selectedYear, selectedQuarter);
  const [selectedTab, setSelectedTab] = useState<AnalyticsIncomeAndOpexTabs>(
    AnalyticsIncomeAndOpexTabs.INCOME
  );

  const handleTabChange = useCallback(
    (_: any, tabIndex: AnalyticsIncomeAndOpexTabs) => {
      setSelectedTab(tabIndex);
    },
    []
  );

  const handleChangeAnalyticsType = useCallback((dataType: ANALYTICS_TYPES) => {
    setSelectedAnalyticsType(dataType);
  }, []);

  const handleYearChange = useCallback(
    (year: string) => {
      setSelectedYear(year);
    },
    [setSelectedYear]
  );

  const handleQuarterChange = useCallback(
    (quarter: QUARTER_OPTIONS) => {
      setSelectedQuarter(quarter);
    },
    [setSelectedQuarter]
  );

  const handleEdit = useCallback(() => {
    // just refetch it, because of the calculation of table data
    updateFilterGroup();
  }, [updateFilterGroup]);

  const chips = useMemo(() => {
    if (!filterGroup) {
      return [];
    }

    const construction_types = _.compact(
      filterGroup.construction_type.map((x) =>
        getConstructionTypeLabelByValue({ value: x })
      )
    );

    return [
      ...filterGroup.cities,
      ...filterGroup.project_size,
      ...filterGroup.project_phase,
      ...construction_types,
    ];
  }, [filterGroup]);

  const handleExcludedDealsUpdate = useCallback(
    (excludedDeals: Array<number> | undefined) => {
      if (filterGroup) {
        updateFilter(filterGroup?.id, {
          excluded_deals: excludedDeals,
        }).then(() => updateFilterGroup());
      }
    },
    [filterGroup, updateFilterGroup]
  );

  const pageHeader = useMemo(() => {
    if (_.isNil(filterGroup)) {
      return <Skeleton variant="text" />;
    }
    return filterGroup.name;
  }, [filterGroup]);

  return (
    <Paper sx={{ paddingTop: 0 }}>
      <Stack
        sx={{
          backgroundColor: colors.blue10,
          borderRadius: "4px",
          marginBottom: 1,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ backgroundColor: colors.blue10 }}
        >
          <PageHeader
            title={pageHeader}
            backLink={URLS.ANALYTICS_INCOME_AND_OPEX_LIST}
            backTitle="Income And OPEX"
          />

          {filterGroup ? (
            <Stack marginRight={2}>
              <EditFilterGroupButton
                filterGroup={filterGroup}
                onSave={handleEdit}
                analyticsType={selectedAnalyticsType}
              />
            </Stack>
          ) : (
            <Skeleton variant="rectangular" width={40} height={40} />
          )}
        </Stack>
        <Stack
          direction="row"
          sx={{
            padding: theme.spacing(0, 2, 2, 2),
          }}
        >
          <Text
            variant="text2"
            sx={{ marginRight: theme.spacing(3), fontWeight: 700 }}
          >
            {filterGroup ? (
              <>{filterGroup.deals.length} projects</>
            ) : (
              <Skeleton variant="text" width={60} />
            )}
          </Text>
          {filterGroup ? (
            <ChipsGroup chips={chips} />
          ) : (
            <>
              {[1, 2, 3].map((x) => (
                <Skeleton
                  key={x}
                  variant="text"
                  width={100}
                  sx={{ marginLeft: theme.spacing(2) }}
                />
              ))}
            </>
          )}
        </Stack>
      </Stack>
      <Box>
        <AnalyticsDetailTabs
          selectedTab={selectedTab}
          onChange={handleTabChange}
          tabs={tabs}
        />
        {filterGroup ? (
          <AnalyticsTable
            deals={filterGroup.deals}
            excludedDeals={filterGroup.excluded_deals}
            dealAvgData={filterGroup.deal_avg_data}
            selectedTab={selectedTab}
            selectedYear={selectedYear}
            onChangeYear={handleYearChange}
            selectedQuarter={selectedQuarter}
            onChangeQuarter={handleQuarterChange}
            selectedAnalyticsType={selectedAnalyticsType}
            onChangeAnalyticsType={handleChangeAnalyticsType}
            loading={loading || isValidating}
            onExcludedDealsUpdate={handleExcludedDealsUpdate}
          />
        ) : (
          <Skeleton
            variant="rectangular"
            sx={{ marginTop: theme.spacing(2) }}
            height={300}
          />
        )}
      </Box>
    </Paper>
  );
};

export default AnalyticsIncomeAndOPEXDetails;
